import { Button, ConfirmModal, Course, MoreButton, ScrollbarContainer, StyledText } from 'UI';
import { TermEnum } from 'UI/PriceLabel/PriceLabel';
import { CourseDto, CourseTypeEnum, PaymentDtoTypeEnum } from 'api/generated';
import { CourseDtoTypeEnum, SubscribeDtoTypeEnum } from 'api/generated';
import { PlusIcon } from 'assets/icons';
import { routes } from 'constant/routes';
import { useAsyncAction, useBundleUpsell, useClientSize, useNotifications } from 'hooks';
import { FC, useState } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css, useTheme } from 'styled-components';
import { respondToHeight, respondToWidth } from 'styles/general/respondTo';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

const MAX_PREVIEW_COUNT = 3;

type CoursesProps = {
  isLoading: boolean;
  isShowChangePlanButton?: boolean;
  title?: string;
  useBillingStudyMaterialsPlanChoice?: boolean;
};

const Courses: FC<CoursesProps> = ({
  isLoading,
  isShowChangePlanButton = true,
  title,
  useBillingStudyMaterialsPlanChoice = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);

  const { successToast, errorToast } = useNotifications();
  const { getIsBreakpoint } = useClientSize();
  const { colors } = useTheme();

  const [isShowAll, setIsShowAll] = useState(false);
  const [isChangePlanChoiceModalOpen, setIsChangePlanChoiceModalOpen] = useState(false);
  const [changePlanCourse, setChangePlanCourse] = useState<CourseDto | null>(null);
  const { handleUpgradeBundle } = useBundleUpsell();

  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const [setActiveCourse, isSetActiveCourseLoading] = useAsyncAction(actions.courses.setActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);

  // Filter out bundles from the courses list to show only individual courses
  const individualCourses = courses?.filter((course) => (course.type as string) !== 'bundle') || [];

  const openChooseProductModal = async (course: CourseDto) => {
    await handleUpgradeBundle(course);
  };

  const isStudyMaterialsSubscriptionCourse = (course: CourseDto) => {
    const hasSubscriptionPricing = Boolean(course.prices?.monthlySubscriptions || course.prices?.annualSubscriptions);

    return (
      course.type === CourseTypeEnum.Default &&
      course.currentPayment?.type === PaymentDtoTypeEnum.Subscription &&
      hasSubscriptionPricing
    );
  };

  const handleChangePlanClick = async (course: CourseDto) => {
    if (useBillingStudyMaterialsPlanChoice && isStudyMaterialsSubscriptionCourse(course)) {
      setChangePlanCourse(course);
      setIsChangePlanChoiceModalOpen(true);
      return;
    }

    await openChooseProductModal(course);
  };

  const handleContinueToChangePlan = async () => {
    if (!changePlanCourse) {
      return;
    }

    setIsChangePlanChoiceModalOpen(false);
    await openChooseProductModal(changePlanCourse);
    setChangePlanCourse(null);
  };

  const handleContinueToUnsubscribe = async () => {
    if (!changePlanCourse) {
      return;
    }

    if (!isStudyMaterialsSubscriptionCourse(changePlanCourse)) {
      setIsChangePlanChoiceModalOpen(false);
      await openChooseProductModal(changePlanCourse);
      setChangePlanCourse(null);
      return;
    }

    const currentLevel = changePlanCourse.currentPayment?.level as SubscribeDtoTypeEnum | undefined;
    const tierKey = String(currentLevel || '').toLowerCase() as 'gold' | 'silver' | 'bronze';
    const monthlySubscriptions = changePlanCourse.prices?.monthlySubscriptions as
      | Record<
          string,
          {
            amount?: number;
            oldAmount?: number;
            defaultCoupon?: { promoCode?: string };
            cancellationPromotionTerms?: {
              discount?: number;
              discountType?: import('api/generated').CouponDiscountType;
              duration?: import('api/generated').CouponSubscriptionDurationType;
              durationInMonths?: number;
            };
          }
        >
      | undefined;
    const currentMonthlyPrice = monthlySubscriptions?.[tierKey];

    if (currentLevel && currentLevel !== SubscribeDtoTypeEnum.None) {
      dispatch(
        actions.courses.selectCourseOptionsForPayment({
          term: TermEnum.MONTHLY,
          type: CourseDtoTypeEnum.Default,
          courseId: changePlanCourse.id,
          subscribeType: currentLevel,
          amount: Number(currentMonthlyPrice?.amount || 0),
          oldAmount: currentMonthlyPrice?.oldAmount,
          isUpdate: false,
          isDowngrade: false,
          couponCode: currentMonthlyPrice?.defaultCoupon?.promoCode,
          discount: currentMonthlyPrice?.cancellationPromotionTerms?.discount,
          discountType: currentMonthlyPrice?.cancellationPromotionTerms?.discountType,
          duration: currentMonthlyPrice?.cancellationPromotionTerms?.duration,
          durationInMonth: currentMonthlyPrice?.cancellationPromotionTerms?.durationInMonths,
        }),
      );
    }

    dispatch(actions.courses.selectCourse(changePlanCourse));
    dispatch(actions.courses.setOpenUnsubscribeOnChooseProductModal(true));
    setIsChangePlanChoiceModalOpen(false);
    setChangePlanCourse(null);
  };

  const handleCloseChangePlanChoiceModal = () => {
    setIsChangePlanChoiceModalOpen(false);
    setChangePlanCourse(null);
  };

  const isWidthSm = getIsBreakpoint('sm');
  const isWidthLs = getIsBreakpoint('ls');

  const isShowGetMoreButton = (individualCourses?.length || 0) > MAX_PREVIEW_COUNT && isWidthSm;
  const sortedCourses = individualCourses?.slice().sort((a, b) => {
    // Active course comes first
    if (a.id === activeCourse?.id) return -1;
    if (b.id === activeCourse?.id) return 1;

    return 0;
  });
  const visibleCourses = isShowAll || !isShowGetMoreButton ? sortedCourses : sortedCourses?.slice(0, MAX_PREVIEW_COUNT);

  const fetchSetActiveCourse = async (courseId: string) => {
    try {
      if (activeCourse?.id !== courseId) {
        await setActiveCourse({ courseId });
        successToast('Your active course successfully set');
      }
    } catch (error) {
      errorToast('Error');
    }
  };

  return (
    <Root>
      {/* Your Courses Section */}
      <CourseSection>
        <Header>
          <div>
            <StyledText as="h3" font={isWidthLs ? 'title_5_bold_20' : 'title_4_bold_24'}>
              {title || 'Your Courses'}
            </StyledText>
            {individualCourses && individualCourses.length > 1 ? (
              <SubtitleText font="body_basic_regular_14">(select the course you want to study)</SubtitleText>
            ) : null}
          </div>
          {!isWidthSm && (
            <StyledButton
              size="small"
              onClick={() => {
                navigate(routes.addNewCourse);
                trackAmplitudeEvent('Add a New Course Pressed');
              }}
              iconComponent={
                <PlusIcon color={isDarkMode ? colors.neutrals[1] : colors.neutrals[11]} width={24} height={24} />
              }>
              Add a new course
            </StyledButton>
          )}
        </Header>
        <List>
          {isLoading ? (
            <ThreeDots width={48} height={48} color={colors.primary[3]} />
          ) : (
            visibleCourses?.map((course, index, courses) => (
              <Course
                key={course.id}
                course={course}
                isShowChangePlanButton={isShowChangePlanButton}
                isLastItem={!!index && index === courses.length - 1}
                onClick={() => fetchSetActiveCourse(course.id)}
                disabled={isSetActiveCourseLoading}
                onStatusClick={() => handleChangePlanClick(course)}
              />
            ))
          )}
          {isShowGetMoreButton && (
            <MoreButton
              text={isShowAll ? 'Less courses' : 'More courses'}
              isShowAll={isShowAll}
              onClick={() => setIsShowAll((prevValue) => !prevValue)}
            />
          )}
        </List>
      </CourseSection>

      {isWidthSm && (
        <Footer>
          <Button
            size="middle"
            onClick={() => {
              navigate(routes.addNewCourse);
              trackAmplitudeEvent('Add a New Course Pressed');
            }}
            iconComponent={<PlusIcon color={colors.neutrals[11]} width={24} height={24} />}>
            Add a new course
          </Button>
        </Footer>
      )}

      <ConfirmModal
        isOpen={isChangePlanChoiceModalOpen}
        onClose={handleCloseChangePlanChoiceModal}
        title="Change plan or unsubscribe?"
        text="Choose Change plan to view pricing options, or Unsubscribe to start cancellation."
        leftButtonTitle="Change plan"
        rightButtonTitle="Unsubscribe"
        onLeftButtonClick={handleContinueToChangePlan}
        onRightButtonClick={handleContinueToUnsubscribe}
      />
    </Root>
  );
};

export default Courses;

const Root = styled.div`
  ${({ theme: { colors } }) => css`
    width: 100%;
    height: 100%;
    min-height: 500px;
    overflow: visible;

    display: flex;
    flex-direction: column;
    gap: 16px;

    padding: 24px;
    padding-right: 8px;

    background-color: ${colors.neutrals[11]};
    border-radius: 12px;

    ${respondToHeight.s`
      min-height: 160px;
    `}

    ${respondToWidth.ls`
      min-height: 160px;
    `}

    ${respondToWidth.sm`
      max-height: none;
      padding: 0;
      padding-bottom: 32px;

      border-radius: 0px;
      border-bottom: 1px solid ${colors.neutrals[9]};
    `}
  `}
`;

const CourseSection = styled.div`
  display: grid;
  grid-template-rows: min-content min-content;
  min-height: auto;
`;

const Header = styled.div`
  width: 100%;
  padding-right: 16px;
  padding-bottom: 24px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  ${respondToWidth.sm`
      padding-right: 0;
      padding-bottom: 16px;
  `}
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const SubtitleText = styled(StyledText)`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[4]};
    margin-top: 4px;
  `}
`;

const List = styled(ScrollbarContainer)`
  padding-right: 8px;
  overflow: visible;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  ${respondToWidth.sm`
    overflow-y: hidden;
    padding-right: 0;
    gap: 16px;
  `}
`;

const Footer = styled.div`
  ${({ theme: { colors } }) => css`
    position: fixed;
    width: 100%;
    left: 0;
    bottom: 0;

    padding: 16px;

    z-index: 1;

    background-color: ${colors.neutrals[11]};
    border-top: 1px solid ${colors.neutrals[9]};
  `}
`;
